import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, fmtDate, useScope } from "../api";
import { useT } from "../i18n";
import { isValidSlug, slugify, useSlug } from "../slug";
import { Field, Msg, SlugInput, useFlash } from "../ui";
import { FeatureMatrix } from "./FeatureMatrix";
import { GuestTabs } from "./GuestTabs";

const STATUSES = ["invited", "configuring", "testing", "live"] as const;
interface ChurchRow {
  id: string;
  name: string;
  slug: string;
  status: (typeof STATUSES)[number];
  defaultLocale: "de" | "en";
  members: number;
  admins: number;
  pendingInvites: number;
  goLiveMissing: string[];
}
interface Invite {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
  acceptedAt: string | null;
}

export function AdminChurches() {
  const { t } = useT();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const q = useQuery({ queryKey: ["admin", "churches", "list"], queryFn: () => api<{ churches: ChurchRow[] }>("/admin/churches") });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "churches"] });

  return (
    <>
      <div className="toolbar">
        <h1>{t("admin.churches.title")}</h1>
        <button className="btn small" onClick={() => setCreating(true)}>
          {t("admin.churches.new")}
        </button>
      </div>
      {creating ? <CreateChurch onClose={() => setCreating(false)} onSaved={invalidate} /> : null}
      {q.isError ? <p className="error">{t("admin.common.error")}</p> : null}
      <GuestTabs />
      <div className="stack">
        {q.data?.churches.map((c) => (
          <ChurchCard key={c.id} c={c} onChanged={invalidate} />
        ))}
      </div>
    </>
  );
}

function CreateChurch({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useT();
  const [f, setF] = useState({ name: "", adminEmail: "", defaultLocale: "de" as "de" | "en" });
  const slugField = useSlug();
  const [flash, run, busy] = useFlash();
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      await run(async () => {
        const slug = slugify(slugField.slug, { trim: true });
        if (!isValidSlug(slug)) throw new Error(t("admin.errors.slug"));
        return api("/admin/churches", { method: "POST", json: { name: f.name, slug, defaultLocale: f.defaultLocale, adminEmail: f.adminEmail || undefined } });
      }, t("admin.churches.created"))
    ) {
      onSaved();
      onClose();
    }
  };
  return (
    <form className="card form" onSubmit={save}>
      <div className="two-col">
        <Field label={t("admin.churches.name")}>
          <input
            required
            minLength={2}
            value={f.name}
            onChange={(e) => {
              const name = e.target.value;
              setF({ ...f, name });
              slugField.syncFrom(name);
            }}
          />
        </Field>
        <Field label={t("admin.churches.slug")}>
          <SlugInput value={slugField.slug} onValue={slugField.setSlugInput} onBlurValue={slugField.blurSlug} />
        </Field>
        <Field label={t("admin.settings.defaultLocale")}>
          <select value={f.defaultLocale} onChange={(e) => setF({ ...f, defaultLocale: e.target.value as "de" | "en" })}>
            <option value="de">{t("admin.common.de")}</option>
            <option value="en">{t("admin.common.en")}</option>
          </select>
        </Field>
        <Field label={t("admin.churches.adminEmail")}>
          <input type="email" value={f.adminEmail} onChange={(e) => setF({ ...f, adminEmail: e.target.value })} />
        </Field>
      </div>
      <div className="actions">
        <button className="btn small" disabled={busy}>
          {t("admin.churches.create")}
        </button>
        <button type="button" className="btn small ghost" onClick={onClose}>
          {t("admin.common.cancel")}
        </button>
      </div>
      <Msg msg={flash} />
    </form>
  );
}

function ChurchCard({ c, onChanged }: { c: ChurchRow; onChanged: () => void }) {
  const { t, locale } = useT();
  const scope = useScope();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "pastor">("admin");
  const [flash, run, busy] = useFlash();
  const invites = useQuery({ queryKey: ["admin", "churches", c.id, "invites"], queryFn: () => api<{ invites: Invite[] }>(`/admin/churches/${c.id}/invites`), enabled: open });

  const setStatus = async (status: ChurchRow["status"]) => {
    if (await run(() => api(`/admin/churches/${c.id}`, { method: "PATCH", json: { status } }), t("admin.churches.statusChanged"))) onChanged();
  };
  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await run(() => api(`/admin/churches/${c.id}/invites`, { method: "POST", json: { email, role } }), t("admin.churches.sent"))) {
      setEmail("");
      onChanged();
      void invites.refetch();
    }
  };

  return (
    <div className="card church">
      <div className="listrow">
        <div className="grow">
          <strong>{c.name}</strong>
          <div className="muted">
            {c.slug} · {t("admin.churches.members")}: {c.members} · {t("admin.churches.admins")}: {c.admins} · {t("admin.churches.pendingInvites")}: {c.pendingInvites}
          </div>
          {c.goLiveMissing.length ? <div className="error">{t("admin.churches.goLiveMissing", { items: c.goLiveMissing.map((m) => t(`admin.churches.${m}`)).join(", ") })}</div> : null}
        </div>
        <span className={`pill ${c.status}`}>{c.status}</span>
        <button className="btn small ghost" onClick={() => scope.setChurchId(c.id)}>
          {t("admin.common.open")}
        </button>
        <button className="btn small ghost" onClick={() => setOpen((v) => !v)}>
          {open ? t("admin.common.close") : t("admin.common.edit")}
        </button>
      </div>
      {open ? (
        <div className="inner">
          <div className="pipeline">
            {STATUSES.map((s) => (
              <button key={s} disabled={busy || s === c.status} className={`step ${s === c.status ? "current" : STATUSES.indexOf(s) < STATUSES.indexOf(c.status) ? "done" : ""}`} onClick={() => setStatus(s)}>
                {s}
              </button>
            ))}
          </div>
          <h3>{t("admin.churches.invites")}</h3>
          <div className="list">
            {invites.data?.invites.map((i) => (
              <div key={i.id} className="listrow">
                <div className="grow">
                  {i.email} <span className="pill">{i.role}</span>
                </div>
                <span className="muted">{i.status === "pending" ? `${t("admin.churches.pending")} · ${t("admin.churches.expires", { when: fmtDate(i.expiresAt, locale) })}` : i.status === "accepted" ? t("admin.churches.accepted") : i.status}</span>
              </div>
            ))}
          </div>
          <FeatureMatrix churchId={c.id} churchName={c.name} />
          <form className="inline-row" onSubmit={invite}>
            <input type="email" required placeholder={t("admin.churches.inviteEmail")} value={email} onChange={(e) => setEmail(e.target.value)} />
            <select value={role} onChange={(e) => setRole(e.target.value as "admin" | "pastor")}>
              <option value="admin">admin</option>
              <option value="pastor">pastor</option>
            </select>
            <button className="btn small" disabled={busy}>
              {t("admin.churches.invite")}
            </button>
          </form>
          <Msg msg={flash} />
        </div>
      ) : null}
    </div>
  );
}
